import * as Sqrl from 'squirrelly';

import {getSizedImageUrl} from '../vendor/theme-scripts/theme-images';
import {getUrlWithVariant} from '../vendor/theme-scripts/theme-product-form';
import {getProductJson} from '../util/fetch-product';
import loadScript from '../util/loader';

import NativeScrollbar from './native-scrollbar';
import {makeSwappers} from './swapper';

const defaults = {
  color: 'ash',
};

const selectors = {
  swatch: 'data-swatch',
  outerGrid: '[data-grid-item]',
  imageSlide: '[data-grid-image]',
  dataGridImageDefault: 'data-grid-image-default',
  dataGridImageTarget: 'data-grid-image-target',
  image: 'data-swatch-image',
  imageId: 'data-swatch-image-id',
  variant: 'data-swatch-variant',
  link: '[data-grid-link]',
  wrapper: '[data-grid-swatches]',
  template: '[data-swatch-template]',
  handle: 'data-swatch-handle',
  label: 'data-swatch-label',
  index: 'data-swatch-index',
  dataMediaId: 'data-media-id',
  dataMediaSrcPlaceholder: 'data-media-src-placeholder',
  dataFetchedImage: 'data-fetched-image',
  dataFetchedImageIndex: 'data-fetched-image-index',
};

const classes = {
  fade: 'is-fade',
};

class ColorMatch {
  constructor(options = {}) {
    this.settings = {
      ...defaults,
      ...options,
    };

    this.match = this.init();
  }

  getColor() {
    return this.match;
  }

  init() {
    const getColors = loadScript({json: window.theme.assets.swatches});
    return getColors
      .then((colors) => {
        return this.matchColors(colors, this.settings.color);
      })
      .catch((e) => {
        console.log('failed to load swatch colors script');
        console.log(e);
      });
  }

  matchColors(colors, name) {
    let bg = '#E5E5E5';
    let img = null;
    const path = window.theme.assets.base || '/';
    const comparisonName = name.toLowerCase().replace(/\s/g, '');
    const array = colors.colors;
    if (array) {
      const variantNameMatch = (nameObject) => {
        const indexName = Object.keys(nameObject).toString();
        const neatName = indexName.toLowerCase().replace(/\s/g, '');
        return neatName === comparisonName;
      };
      const position = array.findIndex(variantNameMatch);
      if (position > -1) {
        const value = Object.values(array[position])[0];
        const valueLowerCase = value.toLowerCase();
        if (valueLowerCase.includes('.jpg') || valueLowerCase.includes('.jpeg') || valueLowerCase.includes('.png') || valueLowerCase.includes('.svg')) {
          img = `${path}${encodeURIComponent(value)}`;
          bg = '#888888';
        } else {
          bg = value;
        }
      }
    }
    return {
      color: this.settings.color,
      path: img,
      hex: bg,
    };
  }
}

class RadioSwatch extends HTMLElement {
  constructor() {
    super();

    this.element = this.querySelector(`[${selectors.swatch}]`);
    this.colorString = this.element.getAttribute(selectors.swatch);
    this.image = this.element.getAttribute(selectors.image);
    this.imageId = this.element.getAttribute(selectors.imageId);
    this.variant = this.element.getAttribute(selectors.variant);
    this.outer = this.element.closest(selectors.outerGrid);
    this.media = null;
    this.imageSlide = null;
    this.imageDefault = null;
    this.stopSlideAnimation = false;
    const matcher = new ColorMatch({color: this.colorString});

    matcher.getColor().then((result) => {
      this.colorMatch = result;
      this.init();
    });
  }

  init() {
    this.setStyles();

    // Change PGI slider image
    if (this.variant && this.outer) {
      // Get images on quickview load
      this.outer.addEventListener('theme:quickview:media', (e) => {
        if (e && e.detail && e.detail.media) {
          this.media = e.detail.media;
        }
      });
    }
  }

  setStyles() {
    if (this.colorMatch.hex) {
      this.element.style.setProperty('--swatch', `${this.colorMatch.hex}`);
    }
    if (this.colorMatch.path) {
      this.element.style.setProperty('background-image', `url(${this.colorMatch.path})`);
      this.element.style.setProperty('background-size', 'cover');
    }
  }

  replaceImage() {
    // Add new loaded image in PGI slider
    if (this.imageReplace && this.imageSlide && this.imageId) {
      if (this.imageSlide.hasAttribute(selectors.dataGridImageTarget) && this.imageSlide.getAttribute(selectors.dataGridImageTarget) !== this.imageId) {
        this.imageSlide.classList.add(classes.fade);
        const timeoutDelay = parseFloat(window.getComputedStyle(this.imageSlide).getPropertyValue('animation-duration')) * 1000;

        setTimeout(() => {
          this.imageSlide.classList.remove(classes.fade);
        }, timeoutDelay);
      }

      this.imageSlide.setAttribute(selectors.dataGridImageTarget, this.imageId);
      this.imageSlide.style.setProperty('background-color', '#fff');

      if (!this.imageSlide.hasAttribute(selectors.dataGridImageDefault)) {
        this.imageSlide.setAttribute(selectors.dataGridImageDefault, window.getComputedStyle(this.imageSlide).backgroundImage);
      }

      this.imageSlide.style.setProperty('background-image', this.imageReplace);
    }
  }
}

class GridSwatch {
  constructor(wrap) {
    this.template = document.querySelector(selectors.template).innerHTML;
    this.wrap = wrap;
    this.handle = wrap.getAttribute(selectors.handle);
    const label = wrap.getAttribute(selectors.label).trim().toLowerCase();
    getProductJson(this.handle).then((product) => {
      this.product = product;
      this.colorOption = product.options.find(function (element) {
        return element.name.toLowerCase() === label || null;
      });
      if (this.colorOption) {
        this.swatches = this.colorOption.values;
        this.init();
      }
    });
  }

  init() {
    this.wrap.innerHTML = '';
    this.swatches.forEach((swatch, index) => {
      let variant = this.product.variants.find((variant) => {
        return variant.options.includes(swatch);
      });
      const image = variant.featured_media ? variant.featured_media.preview_image.src : '';
      const imageId = variant.featured_media ? variant.featured_media.id : '';

      const rand = Math.floor(Math.random() * 9999);

      this.wrap.innerHTML += Sqrl.render(this.template, {
        color: swatch,
        uniq: `${this.product.id}-${variant.id}-${rand}`,
        variant: variant.id,
        product_id: this.product.id,
        image_id: imageId,
        image,
        index,
      });
    });

    new NativeScrollbar(this.wrap);
  }
}

function makeGridSwatches(container) {
  const gridSwatchWrappers = container.querySelectorAll(selectors.wrapper);
  gridSwatchWrappers.forEach((wrap) => {
    new GridSwatch(wrap);
  });
}

const swatchGridSection = {
  onLoad() {
    makeGridSwatches(this.container);
    makeSwappers(this.container);
  },
};

export {swatchGridSection, makeGridSwatches, RadioSwatch};
